<template>
  <div>
    <my-header></my-header>
  
    <div
      v-if="isLoadedNormal"
      class="content-container-level-1"
    >
      <h2>New mesa</h2>

      <form @submit.prevent="createMesaWithConfirmation">

        <b-form-group
          label="Type"
          label-for="type-selector"
          label-class="mb-1"
        >
          <b-form-select
            id="type-selector"
            v-model="type"
            :options="typeOptions"
          />
        </b-form-group>

        <b-form-group
          label="Segment"
          label-for="player-group-selector"
          label-class="mb-1"
        >
          <b-form-select
            id="player-group-selector"
            v-model="playerGroupId"
            :options="playerGroupOptions"
          />
        </b-form-group>

        <hr>

        <div class="mb-4">
          <h5>Start</h5>

          <b-form-datepicker
            v-model="plannedStartDate"
            :min="minPlannedStartDate"
            :start-weekday="MONDAY"
            class="mb-2"
          />

          <time-input
            v-model="plannedStartTime"
          />
        </div>

        <div class="mb-4">
          <h5>Finish</h5>

          <b-form-datepicker
            v-model="plannedFinishDate"
            :min="minPlannedFinishDate"
            :start-weekday="MONDAY"
            class="mb-2"
          />

          <time-input
            v-model="plannedFinishTime"
          />
        </div>

        <div class="mb-3">
          <h5>Duration</h5>
          <p>
            {{durationStr}}
          </p>
        </div>
         <hr>

        <div class="mb-3">
          <h5>Last event's end time</h5>
          <p>
            {{endTimeLastMesaStr}}
          </p>
        </div>

        <hr>

        <h5 class="mb-1">Join prohibition interval</h5>
        <fieldset class="form-group">
          <duration-input v-model="prohibitionInterval"></duration-input>
          <small id="join-available-until" class="form-text text-muted">Join available until: {{ joinAvailableUntil | getMomentMinStr }} (UTC+3)</small>
        </fieldset>

        <h5>Client min version</h5>
        <client-version
          :clientVersionInfo="clientVersionInfo"
          class="mb-2"
        />

        <p class="mb-2">
          {{clientMinVersionComment}}
        </p>
        <input class="form-control" v-model="clientMinVersion" required>

        <hr>

        <b-button :to="{name: 'mesas'}">Cancel</b-button>
        <b-button type="submit" variant="primary">Create</b-button>
      </form>
    </div>

    <loading-indicator
      v-if="isLoading"
    />
  </div>
</template>

<script>
/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import notifier from '../notifier.js';

import { ClientVersionInfo } from '../model/version.js';

/**
 * @typedef { import('../model/event.js').PlayerGroupId } PlayerGroupId
 */
import { DEFAULT_PLAYER_GROUP_ID             } from '../model/event.js';
import { getPlayerGroupOptionsVerbose        } from '../model/event.js';

import { HTTP                                } from '../http-common.js';
import { MESA_TYPE_VALUE_FOSTER_KID_REPEATED } from '../model/mesa.js';
import { createMesaTypeOptions               } from '../model/mesa.js';
import {convertHourToSec, convertMomentToDataStr} from '../util.js';
import { convertMomentToTimeStr              } from '../util.js';
import { getDurationStrBetweenMoments        } from '../util.js';
import { getDurationStrAfterMoment           } from '../util.js';
import { convertDataStrAndTimeStrToLocalData } from '../util.js';
import { normalizeVersionStr                 } from '../util.js';

import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import ClientVersion from './ClientVersion.vue';
import TimeInput from './TimeInput.vue';
import DurationInput from "@/components/DurationInput.vue";

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'MesaAdd',

  components: {
    DurationInput,
    MyHeader,
    LoadingIndicator,
    ClientVersion,
    TimeInput,
  },

  data() {
    const now = new Date();
    const nowDateStr = convertMomentToDataStr(now);
    const nowTimeStr = convertMomentToTimeStr(now);

    const DEFAULT_DURATION_HOURS = 48;

    const DEFAULT_PROHIBITION_DURATION_HOURS = 10;
    const DEFAULT_PROHIBITION_DURATION_SEC = convertHourToSec(DEFAULT_PROHIBITION_DURATION_HOURS);

    const finish = new Date(now.getTime());
    finish.setHours(finish.getHours() + DEFAULT_DURATION_HOURS);
    const finishDateStr = convertMomentToDataStr(finish);
    const finishTimeStr = convertMomentToTimeStr(finish);

    return {
      MONDAY: 1,

      loadState: LOAD_STATE_INIT,

      /** @type {?ClientVersionInfo} */
      clientVersionInfo: null,

      type: null,
      typeOptions: null,

      plannedStartDate: nowDateStr,
      minPlannedStartDate: nowDateStr,
      plannedStartTime: nowTimeStr,

      plannedFinishDate: finishDateStr,
      minPlannedFinishDate: nowDateStr,
      plannedFinishTime: finishTimeStr,

      prohibitionInterval: DEFAULT_PROHIBITION_DURATION_SEC,

      clientMinVersion: '',

      playerGroupId:      DEFAULT_PLAYER_GROUP_ID,
      playerGroupOptions: getPlayerGroupOptionsVerbose(),
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?Date} */
    plannedStartMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedStartDate, this.plannedStartTime);
    },

    /** @returns {?number} */
    plannedStartSec() {
      const plannedStartMoment = this.plannedStartMoment;
      if(plannedStartMoment === null) {
        return null;
      }

      return Math.floor(plannedStartMoment.getTime() / 1000);
    },

    /** @returns {?Date} */
    plannedFinishMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedFinishDate, this.plannedFinishTime);
    },

    /** @returns {?number} */
    plannedFinishSec() {
      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return null;
      }

      return Math.floor(plannedFinishMoment.getTime() / 1000);
    },

    /** @returns {?string} */
    durationStr() {
      const plannedStartMoment = this.plannedStartMoment;
      if(plannedStartMoment === null) {
        return null;
      }

      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return null;
      }

      return getDurationStrBetweenMoments(plannedStartMoment, plannedFinishMoment);
    },

    /** @returns {?string} */
    endTimeLastMesaStr() {
      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return null;
      }

      if(this.type === MESA_TYPE_VALUE_FOSTER_KID_REPEATED) {
        return getDurationStrAfterMoment(plannedFinishMoment, 24 * 60 * 60);
      }
      return getDurationStrAfterMoment(plannedFinishMoment, 48 * 60 * 60);
    },

    joinAvailableUntil() {
      return this.plannedFinishSec - this.prohibitionInterval;
    },

    /** @returns {?string} */
    clientMinVersionComment() {
      const clientVersionInfo = this.clientVersionInfo;
      if(clientVersionInfo === null) {
        return null;
      }

      if(clientVersionInfo.gameVersion === clientVersionInfo.forcedVersion) {
        let message = '';
        message += 'Client min version of new mesa event must be inside this range. ';
        message += 'Server version and forced version are equal. '
        message += 'Consequently, value ' + clientVersionInfo.gameVersion + ' is the only option for min client version.';
        return message;
      }

      return 'Specify client min version of new mesa event inside this range.';
    },
  },

  created() {
    this.loadNewMesaInfo();
  },

  methods: {
    loadNewMesaInfo() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      HTTP.get('/new_mesa_info')
      .then(response => {
        const data = response.data.data;

        const mesaProtoIds = data.mesaProtoIds;
        if(mesaProtoIds.length === 0) {
          notifier.showError('Invalid server response. mesaProtoIds.length === 0.', {sender: this});
          return;
        }

        this.type = mesaProtoIds[0];
        this.typeOptions = createMesaTypeOptions(mesaProtoIds);

        const gameVersion = normalizeVersionStr(data.gameVersion);
        if(gameVersion === null) {
          notifier.showError('Invalid server response. Invalid gameVersion.', {sender: this});
          return;
        }

        const forcedVersion = data.forcedVersion;

        this.clientVersionInfo = new ClientVersionInfo(gameVersion, forcedVersion);

        this.clientMinVersion = forcedVersion;

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    createMesaWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const mesaDefInputReport = this.getMesaDefInputReport();
      if(mesaDefInputReport.error !== null) {
        notifier.showError(mesaDefInputReport.error.message, {sender: this});
        return;
      }

      const mesaDef = mesaDefInputReport.mesaDef;
      if(mesaDef === null) {
        notifier.showError('Invalid input report. mesaDef === null.', {sender: this});
        return;
      }

      this.confirmMesaCreation(() => {
        this.createMesa(mesaDef);
      });
    },

    getMesaDefInputReport() {
      const plannedStartSec = this.plannedStartSec;
      if(plannedStartSec === null) {
        return new MesaDefInputReport(
          null,
          new Error('Start time must be specified.')
        );        
      }

      const plannedFinishSec = this.plannedFinishSec;
      if(plannedFinishSec === null) {
        return new MesaDefInputReport(
          null,
          new Error('Finish time must be specified.')
        );
      }

      if(plannedFinishSec === plannedStartSec) {
        return new MesaDefInputReport(
          null,
          new Error('Mesa duration is zero.')
        );
      }

      if(plannedFinishSec < plannedStartSec) {
        return new MesaDefInputReport(
          null,
          new Error('Finish time must be after start time.')
        );
      }

      const trimmedClientMinVersion = this.clientMinVersion.trim();
      if(trimmedClientMinVersion.length === 0) {
        return new MesaDefInputReport(
          null,
          new Error('Client min version must be specified.')
        );
      }

      const mesaDef = new MesaDef(
        this.type,
        plannedStartSec,
        plannedFinishSec,
        trimmedClientMinVersion,
        this.playerGroupId,
        this.prohibitionInterval,
      );
      return new MesaDefInputReport(mesaDef, null);
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmMesaCreation(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to create this mesa {env}?',
        'Create',
        onConfirmed,
      );
    },

    /**
     * @param {MesaDef} mesaDef
     */
    createMesa(mesaDef) {
      const data = new FormData();
      data.set('type',                    mesaDef.type.toString());
      data.set('stime',                   mesaDef.plannedStartSec.toString());
      data.set('ftime',                   mesaDef.plannedFinishSec.toString());
      data.set('client_min_version',      mesaDef.clientMinVersion);
      data.set('player_group_id',         mesaDef.playerGroupId.toString());
      data.set('prohibition_interval',    mesaDef.prohibitionInterval.toString());

      HTTP.post('/mesa/add', data)
      .then(response => {
        const result = response.data.result.code;

        const RESULT_SUCCESS           = 1;
        const RESULT_SCHEDULE_CONFLICT = 2;

        if(result === RESULT_SUCCESS) {
          notifier.showSuccessAfterTransition('Mesa created.');
          this.$router.push({name: 'mesas'});
          return;
        }

        if(result === RESULT_SCHEDULE_CONFLICT) {
          notifier.showError('Can not schedule mesa. Schedule conflict.', {sender: this});
          return;
        }

        notifier.showError(`Unknown result code ${result}.`, {sender: this});
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },
  },
};


class MesaDefInputReport {
  /**
   * @param {?MesaDef} mesaDef
   * @param {?Error} error
   */
  constructor(mesaDef, error) {
    this.mesaDef = mesaDef;
    this.error   = error;
  }
}

class MesaDef {
  /**
   * @param {number} type
   * @param {number} plannedStartSec
   * @param {number} plannedFinishSec
   * @param {string} clientMinVersion
   * @param {PlayerGroupId} playerGroupId
   * @param {number} prohibitionInterval
   */
  constructor(
    type,
    plannedStartSec,
    plannedFinishSec,
    clientMinVersion,
    playerGroupId,
    prohibitionInterval
  ) {
    this.type             = type;
    this.plannedStartSec  = plannedStartSec;
    this.plannedFinishSec = plannedFinishSec;
    this.clientMinVersion = clientMinVersion;
    this.playerGroupId    = playerGroupId;
    this.prohibitionInterval = prohibitionInterval;
  }
}
</script>
